import { QuestionType, ScoringMode, PointsMode } from "../state/phases.js";
export function normalizeTypedAnswer(s) {
    return s.trim().toLowerCase().replace(/\s+/g, " ");
}
function levenshtein(a, b) {
    const m = a.length;
    const n = b.length;
    const row = Array.from({ length: n + 1 }, (_, i) => i);
    for (let i = 1; i <= m; i++) {
        let prev = i;
        for (let j = 1; j <= n; j++) {
            const next = a[i - 1] === b[j - 1]
                ? row[j - 1]
                : 1 + Math.min(prev, row[j], row[j - 1]);
            row[j - 1] = prev;
            prev = next;
        }
        row[n] = prev;
    }
    return row[n];
}
function fuzzyEditBudget(acceptedAnswer) {
    const len = acceptedAnswer.length;
    if (len <= 3)
        return 0;
    if (len <= 6)
        return 1;
    return 2;
}
export function fuzzyMatchTypedAnswer(submitted, acceptedAnswers) {
    const norm = normalizeTypedAnswer(submitted);
    return acceptedAnswers.some((accepted) => {
        if (norm === accepted)
            return true;
        const budget = fuzzyEditBudget(accepted);
        return budget > 0 && levenshtein(norm, accepted) <= budget;
    });
}
function checkCorrectness(input) {
    const { questionType, correctOptionIds, acceptedAnswers, submittedOptionIds, submittedText, } = input;
    switch (questionType) {
        case QuestionType.SLIDE:
            return { isCorrect: false, normalizedSubmission: null };
        case QuestionType.QUIZ_SINGLE:
        case QuestionType.TRUE_FALSE: {
            if (submittedOptionIds.length !== 1)
                return { isCorrect: false, normalizedSubmission: null };
            return {
                isCorrect: submittedOptionIds[0] === correctOptionIds[0],
                normalizedSubmission: null,
            };
        }
        case QuestionType.QUIZ_MULTI: {
            const submitted = new Set(submittedOptionIds);
            const correct = new Set(correctOptionIds);
            if (submitted.size !== correct.size)
                return { isCorrect: false, normalizedSubmission: null };
            const isCorrect = [...correct].every((id) => submitted.has(id));
            return { isCorrect, normalizedSubmission: null };
        }
        case QuestionType.TYPE_ANSWER: {
            const normalized = submittedText !== null ? normalizeTypedAnswer(submittedText) : "";
            const isCorrect = submittedText !== null &&
                fuzzyMatchTypedAnswer(submittedText, acceptedAnswers);
            return { isCorrect, normalizedSubmission: normalized };
        }
        default:
            return { isCorrect: false, normalizedSubmission: null };
    }
}
function computeTimeWeightedPoints(basePoints, timerDurationMs, responseTimeMs) {
    // A non-positive timer would make the speed ratio NaN, and NaN points fail
    // the integer column they are written to. Award the base instead: with no
    // window to be fast in, there is nothing to weight by.
    if (!(timerDurationMs > 0))
        return basePoints;
    if (responseTimeMs <= 500)
        return basePoints;
    const ratio = (timerDurationMs - responseTimeMs) / timerDurationMs;
    const points = Math.round(basePoints * 0.5 * (1 + ratio));
    const min = Math.round(basePoints * 0.5);
    return Math.max(min, Math.min(basePoints, points));
}
export function scoreAnswer(input) {
    const { questionType, isLate, scoringMode, pointsMode } = input;
    const rawBase = input.basePoints ?? 1000;
    if (questionType === QuestionType.SLIDE) {
        return {
            isCorrect: false,
            points: 0,
            maxPoints: 0,
            reason: "slide",
            normalizedSubmission: null,
        };
    }
    const { isCorrect, normalizedSubmission } = checkCorrectness(input);
    if (isLate) {
        return {
            isCorrect,
            points: 0,
            maxPoints: rawBase,
            reason: "late",
            normalizedSubmission,
        };
    }
    if (pointsMode === PointsMode.NONE) {
        return {
            isCorrect,
            points: 0,
            maxPoints: 0,
            reason: "no_points",
            normalizedSubmission,
        };
    }
    const basePoints = pointsMode === PointsMode.DOUBLE ? rawBase * 2 : rawBase;
    if (!isCorrect) {
        return {
            isCorrect: false,
            points: 0,
            maxPoints: basePoints,
            reason: "incorrect",
            normalizedSubmission,
        };
    }
    const points = scoringMode === ScoringMode.CLASSIC
        ? computeTimeWeightedPoints(basePoints, input.timerDurationMs, input.responseTimeMs)
        : basePoints;
    return {
        isCorrect: true,
        points,
        maxPoints: basePoints,
        reason: "correct",
        normalizedSubmission,
    };
}
//# sourceMappingURL=engine.js.map